import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Mail, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Reveal, SectionHead } from "./shared";

const QUESTIONS = [
  {
    q: "È il tramonto sul Lago d'Orta. Dove ti troviamo?",
    answers: [
      { t: "In un luogo silenzioso, a ringraziare per la giornata", c: "san-giulio" },
      { t: "Nascosto, a osservare gli altri da lontano", c: "drago" },
      { t: "Con la persona che amo, anche se il mondo non approva", c: "elisabetta" },
      { t: "A smontare qualcosa per capire come funziona", c: "adele-matilde" },
    ],
  },
  {
    q: "Ti viene affidato un segreto importante. Tu…",
    answers: [
      { t: "Lo custodisco per sempre, costi quel che costi", c: "drago" },
      { t: "Lo trasformo in una missione", c: "san-giulio" },
      { t: "Lo uso per cambiare il mio destino", c: "elvira" },
      { t: "Lo studio finché non lo capisco", c: "adele-matilde" },
    ],
  },
  {
    q: "La persona che ami è in pericolo. Cosa fai?",
    answers: [
      { t: "Sacrifico tutto, anche me stesso", c: "elisabetta" },
      { t: "Sparisco per proteggerla", c: "marco" },
      { t: "Esco dall'ombra e affronto chi la minaccia", c: "drago" },
      { t: "Cerco una soluzione che nessuno ha mai provato", c: "adele-matilde" },
    ],
  },
  {
    q: "Il tuo potere più grande?",
    answers: [
      { t: "La fede che muove le acque", c: "san-giulio" },
      { t: "La pazienza di chi ha secoli davanti", c: "drago" },
      { t: "L'istinto di sopravvivenza", c: "marco" },
      { t: "Il fascino che piega gli altri alla mia volontà", c: "elvira" },
    ],
  },
  {
    q: "Cosa vedi guardando il lago?",
    answers: [
      { t: "Una promessa da mantenere", c: "san-giulio" },
      { t: "Un amore che il tempo non ha spento", c: "elisabetta" },
      { t: "Una seconda possibilità", c: "marco" },
      { t: "Il riflesso di ciò che desidero, e che avrò", c: "elvira" },
    ],
  },
];

const RESULTS = {
  "san-giulio": {
    name: "San Giulio",
    role: "Il Santo delle Acque",
    blurb: "Fede incrollabile e missioni impossibili: tu non aspetti la barca, trovi il modo di attraversare.",
  },
  drago: {
    name: "Il Drago del Lago",
    role: "La Creatura Custode",
    blurb: "Paziente, fedele, profondo: custodisci tesori che nessuno immagina e sai aspettare il momento giusto.",
  },
  elisabetta: {
    name: "Elisabetta",
    role: "La Ribelle del 1600",
    blurb: "Il tuo cuore non accetta compromessi: scegli l'amore anche quando costa caro. È la tua libertà.",
  },
  elvira: {
    name: "Donna Elvira",
    role: "La Strega del Lago",
    blurb: "Magnetismo puro: hai una volontà che piega gli ostacoli. Usala per il bene — il lago ricorda tutto.",
  },
  marco: {
    name: "Marco",
    role: "Il Ragazzo del 1989",
    blurb: "Sei rimasto in fondo senza smettere di essere te stesso: la tua seconda possibilità è già cominciata.",
  },
  "adele-matilde": {
    name: "Adele & Matilde",
    role: "Le Alleate del 2025",
    blurb: "Bontà e scienza: accogli chi è diverso e non ti fermi finché non hai capito. Ogni enigma ti chiama.",
  },
};

const inputCls =
  "w-full rounded-full border border-gold/30 bg-lake-deep/60 px-5 py-3 text-sm text-parchment-ivory placeholder:text-parchment-ivory/40 transition-colors focus:border-gold-shimmer focus:outline-none";

export default function Quiz() {
  const [step, setStep] = useState(-1); // -1 intro, 0..4 domande, 5 email, 6 fatto
  const [scores, setScores] = useState({});
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [loading, setLoading] = useState(false);

  const winner = useMemo(() => {
    let best = null;
    let max = -1;
    for (const [c, n] of Object.entries(scores)) {
      if (n > max) {
        max = n;
        best = c;
      }
    }
    return best || "drago";
  }, [scores]);

  const answer = (c) => {
    setScores((s) => ({ ...s, [c]: (s[c] || 0) + 1 }));
    setStep((s) => s + 1);
  };

  const restart = () => {
    setStep(-1);
    setScores({});
    setEmail("");
    setNome("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/quiz", { email, nome: nome || undefined, personaggio: winner });
      setStep(6);
    } catch {
      toast.error("Qualcosa è andato storto. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  const result = RESULTS[winner];

  return (
    <section id="quiz" className="relative overflow-hidden bg-lake-deep py-20 lg:py-32" data-testid="quiz-section">
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-lake-teal/20 via-transparent to-lake-teal/10" />
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          kicker="Il Quiz del Lago"
          title="Quale personaggio del Segreto sei?"
          sub="Cinque domande, sedici secoli di destini. Scopri chi sei nel romanzo: il risultato arriva via email e vale un regalo."
        />

        <Reveal className="mt-12">
          <div className="rounded-xl border border-gold/25 bg-lake-teal/20 p-8 backdrop-blur-sm sm:p-10">
            <AnimatePresence mode="wait">
              {step === -1 && (
                <motion.div
                  key="intro"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  className="text-center"
                >
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold/15">
                    <Sparkles className="h-6 w-6 text-gold-shimmer" />
                  </div>
                  <p className="mt-6 text-sm leading-relaxed text-parchment-ivory/70 sm:text-base">
                    Il Santo, il Drago, la Ribelle, la Strega, il Ragazzo, le Alleate: sei destini, uno
                    somiglia al tuo. Rispondi d'istinto, come chi si affaccia sull'acqua.
                  </p>
                  <p className="mx-auto mt-5 flex max-w-md items-start gap-2.5 rounded-lg border border-gold/25 bg-gold/10 p-4 text-left text-sm leading-relaxed text-parchment-ivory/85">
                    <Gift className="mt-0.5 h-4 w-4 shrink-0 text-gold-shimmer" />
                    <span>
                      Lascia la tua email: ti inviamo il risultato e, se lo presenti a una delle
                      presentazioni del libro — le prossime sono nella sezione Eventi — ricevi
                      in omaggio un gadget della saga.
                    </span>
                  </p>
                  <button
                    data-testid="quiz-start-button"
                    onClick={() => setStep(0)}
                    className="mt-8 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-ink-dark transition-transform duration-300 hover:-translate-y-0.5"
                  >
                    Inizia il viaggio
                  </button>
                </motion.div>
              )}

              {step >= 0 && step < QUESTIONS.length && (
                <motion.div
                  key={`q-${step}`}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.35 }}
                >
                  <p className="text-xs uppercase tracking-[0.3em] text-gold" data-testid="quiz-progress">
                    Domanda {step + 1} di {QUESTIONS.length}
                  </p>
                  <h3 className="mt-3 font-serif text-2xl text-parchment-ivory" data-testid="quiz-question">
                    {QUESTIONS[step].q}
                  </h3>
                  <div className="mt-7 space-y-3">
                    {QUESTIONS[step].answers.map((a, i) => (
                      <button
                        key={i}
                        data-testid={`quiz-answer-${i}`}
                        onClick={() => answer(a.c)}
                        className="w-full rounded-lg border border-gold/25 bg-lake-deep/50 px-5 py-4 text-left text-sm text-parchment-ivory/85 transition-colors duration-300 hover:border-gold-shimmer hover:bg-lake-teal/40"
                      >
                        {a.t}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {step === QUESTIONS.length && (
                <motion.div
                  key="email"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  className="text-center"
                >
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold/15">
                    <Mail className="h-6 w-6 text-gold-shimmer" />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl text-parchment-ivory">
                    Il lago ha scelto. Dove ti inviamo il verdetto?
                  </h3>
                  <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-parchment-ivory/70">
                    Scrivici la tua email: riceverai il tuo personaggio e la email varrà come invito per
                    ritirare il gadget in omaggio alle presentazioni del libro.
                  </p>
                  <form onSubmit={submit} className="mx-auto mt-7 flex max-w-md flex-col gap-3" data-testid="quiz-email-form">
                    <input
                      data-testid="quiz-name-input"
                      type="text"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      placeholder="Il tuo nome (facoltativo)"
                      className={inputCls}
                    />
                    <input
                      data-testid="quiz-email-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="La tua email"
                      className={inputCls}
                    />
                    <button
                      data-testid="quiz-submit-button"
                      type="submit"
                      disabled={loading}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-ink-dark transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                      {loading ? "Il lago sta scrivendo…" : "Rivelami il mio personaggio"}
                      <Send className="h-4 w-4" />
                    </button>
                  </form>
                </motion.div>
              )}

              {step === 6 && (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center"
                  data-testid="quiz-result"
                >
                  <p className="text-xs uppercase tracking-[0.3em] text-gold">Il lago ha parlato: tu sei</p>
                  <h3 className="mt-3 font-serif text-3xl text-gold-shimmer sm:text-4xl">{result.name}</h3>
                  <p className="mt-1 text-sm font-medium text-parchment-ivory/70">{result.role}</p>
                  <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-parchment-ivory/80">
                    {result.blurb}
                  </p>
                  <p className="mx-auto mt-6 flex max-w-md items-start gap-2.5 rounded-lg border border-gold/25 bg-gold/10 p-4 text-left text-sm leading-relaxed text-parchment-ivory/85">
                    <Gift className="mt-0.5 h-4 w-4 shrink-0 text-gold-shimmer" />
                    <span>
                      Controlla la tua casella: la email che hai ricevuto vale un gadget in omaggio se la
                      presenti a una delle presentazioni del romanzo. Le prossime date le trovi nella
                      sezione Eventi del sito.
                    </span>
                  </p>
                  <button
                    data-testid="quiz-restart-button"
                    onClick={restart}
                    className="mt-8 rounded-full border border-gold/40 px-7 py-3 text-sm font-semibold text-gold-shimmer transition-colors duration-300 hover:border-gold-shimmer"
                  >
                    Fai fare il quiz a un amico
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
